
import BookingWidget from "@/components/BookingWidget";
import { useQuery } from "@tanstack/react-query";
import { getNightlyRates } from "@/services/pricingService";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import { useReveal } from "@/hooks/useReveal";
import { Check } from "lucide-react";

const BookingSection = () => {
  const { data: nightlyRates } = useQuery({
    queryKey: ["nightly-rates"],
    queryFn: getNightlyRates,
  });

  const content = {
    heading: "Planifiez Votre Séjour de Rêve",
    description:
      "Réservez dès maintenant votre séjour à la villa Douce Vague et profitez d'une expérience inoubliable au cœur du Grau du Roi.",
    whyChooseTitle: "Pourquoi choisir la Villa Douce Vague ?",
    feature1: "Villa 140m² spacieuse et moderne avec piscine privée chauffée",
    feature2: "Emplacement idéal à quelques pas des plages et des commerces",
    feature3: "Équipements premium pour un confort optimal",
    feature4: "Parfait pour les familles et les groupes d'amis",
    feature5: "À proximité de nombreuses activités et sites touristiques",
    ratesTitle: "À partir de",
    highSeason: "Haute saison (juillet-août)",
    midSeason: "Moyenne saison (mai-juin, sept.)",
    lowSeason: "Basse saison",
    minStay: "Séjour minimum de 7 nuits en haute saison",
    perNight: "/nuit",
  };

  const { translatedContent } = useTranslatedContent(content);

  const getSeasonPrices = () => {
    if (!nightlyRates || nightlyRates.length === 0) {
      return { highSeason: "350€", midSeason: "250€", lowSeason: "180€" };
    }
    const highSeasonRate = nightlyRates.find((r) => {
      const m = new Date(r.start_date).getMonth();
      return m >= 5 && m <= 7;
    });
    const midSeasonRate = nightlyRates.find((r) => {
      const m = new Date(r.start_date).getMonth();
      return (m >= 4 && m <= 5) || m === 8;
    });
    const lowSeasonRate = nightlyRates.find((r) => {
      const m = new Date(r.start_date).getMonth();
      return m <= 3 || m >= 9;
    });
    return {
      highSeason: highSeasonRate ? `${highSeasonRate.price_per_night}€` : "350€",
      midSeason: midSeasonRate ? `${midSeasonRate.price_per_night}€` : "250€",
      lowSeason: lowSeasonRate ? `${lowSeasonRate.price_per_night}€` : "180€",
    };
  };

  const prices = getSeasonPrices();

  const features = [
    translatedContent.feature1 || content.feature1,
    translatedContent.feature2 || content.feature2,
    translatedContent.feature3 || content.feature3,
    translatedContent.feature4 || content.feature4,
    translatedContent.feature5 || content.feature5,
  ];

  const headerReveal = useReveal<HTMLDivElement>();
  const contentReveal = useReveal<HTMLDivElement>();

  return (
    <section className="relative overflow-hidden bg-villa-blue">
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-32">

        {/* Section header */}
        <div
          ref={headerReveal.ref}
          className={`text-center mb-16 reveal ${headerReveal.revealed ? "in" : ""}`}
        >
          <p className="text-xs font-medium tracking-[0.18em] uppercase text-villa-turquoise-light mb-4">
            Réservation
          </p>
          <h2 className="heading-lg text-white mb-4">
            {translatedContent.heading || content.heading}
          </h2>
          <p className="body-lg text-white/60 max-w-2xl mx-auto">
            {translatedContent.description || content.description}
          </p>
        </div>

        {/* Content grid */}
        <div
          ref={contentReveal.ref}
          className={`grid grid-cols-1 lg:grid-cols-5 gap-8 reveal ${contentReveal.revealed ? "in" : ""}`}
        >
          {/* Left: Features + Pricing */}
          <div className="lg:col-span-3 space-y-10">

            {/* Features */}
            <div>
              <h3 className="text-white font-semibold text-lg mb-6">
                {translatedContent.whyChooseTitle || content.whyChooseTitle}
              </h3>
              <ul className="space-y-4">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-villa-turquoise/20 flex items-center justify-center mt-0.5">
                      <Check className="h-3 w-3 text-villa-turquoise-light" strokeWidth={2.5} />
                    </span>
                    <span className="text-white/70 text-[15px] leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pricing */}
            <div>
              <h3 className="text-white font-semibold text-lg mb-6">
                {translatedContent.ratesTitle || content.ratesTitle}
              </h3>
              <div className="space-y-3">
                {[
                  { label: translatedContent.highSeason || content.highSeason, price: prices.highSeason, highlight: true },
                  { label: translatedContent.midSeason || content.midSeason, price: prices.midSeason, highlight: false },
                  { label: translatedContent.lowSeason || content.lowSeason, price: prices.lowSeason, highlight: false },
                ].map((row, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-5 py-4 rounded-2xl transition-colors ${
                      row.highlight
                        ? "bg-villa-turquoise/20 border border-villa-turquoise/30"
                        : "bg-white/[0.05] border border-white/[0.06]"
                    }`}
                  >
                    <span className={`text-sm ${row.highlight ? "text-white" : "text-white/60"}`}>
                      {row.label}
                    </span>
                    <div className="flex items-baseline gap-1">
                      <span className={`font-playfair font-bold text-xl ${row.highlight ? "text-white" : "text-white/80"}`}>
                        {row.price}
                      </span>
                      <span className="text-white/40 text-xs">{translatedContent.perNight || content.perNight}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-white/40 text-xs">{translatedContent.minStay || content.minStay}</p>
            </div>
          </div>

          {/* Right: Booking widget */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl shadow-black/30">
              <BookingWidget />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
